import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import { requestSignInViaEmailLink } from "services/firebase/auth";
import { logRequestSignInWithEmailLink } from "services/firebase/auth/analytics/signInViaEmailLink";
import firebaseErrorTranslator, {
  ErrorMessage as ErrorMessageType
} from "services/firebase/auth/errorTranslator";

import { getReferralCodeFromLocation } from "seneca-common/utils/functions/referral";

import { getCallbackUrl } from "features/passwordless-sign-in/SignInCheckYourEmailRoute";
import { ROUTES } from "routing/routes";

import { signInWithPassword } from "../components/utils/signInWithPassword";

type Props = {
  prefilledEmail?: string;
  onSignInWithPasswordSuccess?: () => void;
  onSignInError?: (error: Error) => void;
  redirectAuthenticatedUserTo?: string;
};

type SignInWithEmailAndPasswordForm = {
  email: string;
  password?: string;
};

const REQUEST_EMAIL_LINK_ERROR_MESSAGE =
  "Something went wrong, please try again.";

export default function useSignInWithEmailAndOptionalPasswordForm({
  prefilledEmail,
  onSignInWithPasswordSuccess,
  onSignInError,
  redirectAuthenticatedUserTo: redirectAuthenticatedUserToLocation
}: Props) {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<
    ErrorMessageType | undefined
  >();

  const form = useForm<SignInWithEmailAndPasswordForm>({
    defaultValues: { email: prefilledEmail },
    mode: "onSubmit"
  });

  const hasStartedTypingPassword = !!form.watch("password");

  const onSubmitEmailAndPassword: SubmitHandler<
    Required<SignInWithEmailAndPasswordForm>
  > = async ({ email, password }) => {
    const referralCode = getReferralCodeFromLocation(location);

    await dispatch(
      signInWithPassword({
        email,
        password,
        referralCode,
        onSignInSuccess: onSignInWithPasswordSuccess,
        onSignInError: error => {
          const errorMessage = firebaseErrorTranslator(error);
          setErrorMessage(errorMessage);
          onSignInError?.(error);
        }
      })
    );
  };

  const onRequestEmailLink: SubmitHandler<
    SignInWithEmailAndPasswordForm
  > = async ({ email }) => {
    try {
      logRequestSignInWithEmailLink({
        email
      });

      await requestSignInViaEmailLink({
        email,
        callbackUrl: getCallbackUrl({
          redirectAuthenticatedUserToLocation,
          signInLocation: location
        })
      });

      navigate(ROUTES.SIGN_IN_CHECK_YOUR_EMAIL.buildPath({}), {
        state: {
          email,
          redirectAuthenticatedUserToLocation,
          signInLocation: location
        }
      });
    } catch (err) {
      const error = err as Error;

      setErrorMessage({ message: REQUEST_EMAIL_LINK_ERROR_MESSAGE });
      onSignInError?.(error);
    }
  };

  const onSubmit = hasStartedTypingPassword
    ? onSubmitEmailAndPassword
    : onRequestEmailLink;

  const submitButtonText = hasStartedTypingPassword
    ? "Log in"
    : "Send one-time email link";

  return {
    form,
    errorMessage,
    submitButtonText,
    onSubmit: form.handleSubmit(onSubmit)
  };
}
